import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Auth } from '../../providers/auth';
import { Api } from '../../providers/api';
import { Plan, PaymentProvider } from '../../providers/models';
import {
  canonicalTier as resolveCanonicalTier,
  taglineFor as taglineForPlan,
  featuresFor as featuresForPlan,
  priceDisplay as priceDisplayFor,
  annualTotal as annualTotalFor,
  payloadDescription,
  describeFromPayload
} from '../../providers/plan-display';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/throw';
import { Observable } from 'rxjs/Observable';

export enum WizardStep {
  DATOS = 0,
  PLAN = 1,
  CONFIRM = 2
}

// Bullet copy single source (Q1: this api/store layer owns the copy; no
// landing code lives in this repo so the map stays here).
// Q6-DEFAULT: no Reels bullet — no feature flag maps to it.
export const BULLET_MAP: { [key: string]: string } = {
  zocalos: 'Zócalos personalizados en tus pantallas',
  multicaja: 'Modo multicaja para varias pantallas',
  franjasHorarias: 'Programación por franjas horarias',
  monitoreo: 'Monitoreo del estado de tus pantallas'
};

// Landing parity (wizard cards mirror the landing pricing cards).
// Display copy lives in providers/plan-display (single source shared with
// /pago); backend C1 `description` / `featuresDisplay` are primary, the
// tier maps are the fallback. Re-exported here so existing consumers keep
// working (specs reference them off this module).
export { TIER_TAGLINES, TIER_FEATURES } from '../../providers/plan-display';

@Component({
  selector: 'app-register-wizard',
  templateUrl: './register-wizard.component.html',
  styleUrls: ['./register-wizard.component.css']
})
export class RegisterWizardComponent implements OnInit {

  WizardStep = WizardStep;
  currentStep: WizardStep = WizardStep.DATOS;

  isLoading = false;
  errorMsg: string = null;
  showPassword = false;

  form: FormGroup;

  selectedPlanMode: 'trial' | 'paid' = 'trial';
  selectedPlan: Plan | null = null;
  selectedProvider: PaymentProvider | null = null;
  paymentId: string | null = null;
  paypalPaymentId: string | null = null;

  availablePlans: Plan[] = [];
  billingCycle: 'mensual' | 'anual' = 'mensual';
  paypalClientId = '';
  paypalCurrency = 'USD';
  loadingPlans = true;
  plansLoadError: string | null = null;
  isLoadingPayPal = false;

  availableProviders: PaymentProvider[] = [];
  paypalLoaded = false;

  trialEndsAt: Date = null;
  responseData: any = null;
  trialDaysRemaining = 7;

  // Confirmation data (R3): plan name from the chosen C1 payload plus the
  // created circuito identity surfaced by the 201 response.
  confirmedPlanName: string = null;
  createdCircuitoPlanId: string = null;
  createdCircuitoGuid: string = null;

  // SelfServe listing filter (R3) + billing-cycle filter (tabs fix):
  // only plans available for online signup, excluding franquicia, and
  // matching the selected billing tab. No id-based exclusion (Q4-DEFAULT).
  get filteredPlans(): Plan[] {
    var cycle: string = this.billingCycle || 'mensual';
    return this.availablePlans.filter(function(p: any) {
      if (p.selfServe === false || p.tier === 'franquicia') { return false; }
      var billing: string = p.billing || p.billingCycle || 'mensual';
      return billing === cycle;
    }.bind(this));
  }

  // Payload-driven description (tabs/descriptions fix): prefers backend
  // `description` when present, otherwise derives a neutral line from
  // payload fields only (maxScreens). No hardcoded per-plan marketing copy,
  // so frozen C1 stays intact and every card always shows a description.
  planDescription(plan: Plan): string {
    if (!plan) { return ''; }
    return payloadDescription(plan) || describeFromPayload(plan);
  }

  // Q2-DEFAULT (T9 DoD): prominent per-month derived math removed; the
  // payload amount is the single source of truth for prices.
  get paypalAmount(): string {
    if (!this.selectedPlan) return '0.00';
    return this.selectedPlan.amount || '0.00';
  }

  get taxAmount(): string {
    if (!this.selectedPlan) return '0.00';
    var p: any = this.selectedPlan;
    if (p.taxAmount) return Number(p.taxAmount).toFixed(2);
    if (p.taxRate) { var amt = Number(p.amount || 0); var t = amt * Number(p.taxRate) / 100; return t.toFixed(2); }
    if (p.tax) return Number(p.tax).toFixed(2);
    return '0.00';
  }

  get totalAmount(): string {
    if (!this.selectedPlan) return '0.00';
    var amt = Number(this.paypalAmount || 0);
    var tax = Number(this.taxAmount || 0);
    return (amt + tax).toFixed(2);
  }

  get hasTax(): boolean { return Number(this.taxAmount) > 0; }

  get canRenderPayPal(): boolean {
    return !!this.selectedPlan && !!this.selectedProvider && this.selectedProvider.id === 'paypal' && !!this.selectedProvider.clientId;
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private auth: Auth,
    private api: Api
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });
    var preview = new Date();
    preview.setDate(preview.getDate() + 7);
    this.trialEndsAt = preview;
    if (typeof window !== 'undefined' && (window as any).paypal) {
      this.paypalLoaded = true;
    }
    this.loadPlans();
  }

  private loadPlans(): void {
    this.loadingPlans = true;
    this.plansLoadError = null;
    this.api.getPlans().subscribe(function(res: any) {
      var data = res && res.data ? res.data : res;
      var plans = data.plans || [];
      this.availablePlans = Array.isArray(plans) ? plans : [];
      if (!this.selectedPlan && this.selectedPlanMode === 'paid' && this.filteredPlans.length > 0) {
        this.selectedPlan = this.filteredPlans[0];
        this.paypalCurrency = this.selectedPlan.currency || this.paypalCurrency || 'USD';
      }
      this.loadingPlans = false;
    }.bind(this), function() {
      this.loadingPlans = false;
      this.plansLoadError = 'No se pudieron cargar los planes. Intente nuevamente.';
    }.bind(this));
  }

  retryPlans(): void { this.loadPlans(); }

  // Linear wizard navigation with validation per step
  next(): void {
    this.errorMsg = null;
    if (this.currentStep === WizardStep.DATOS) {
      if (this.form.invalid) {
        Object.keys(this.form.controls).forEach(function(key) { this.form.get(key).markAsTouched(); }.bind(this));
        this.errorMsg = 'Completa nombre, email y contraseña.';
        return;
      }
      this.currentStep = WizardStep.PLAN;
      return;
    }
    if (this.currentStep === WizardStep.PLAN) {
      if (this.selectedPlanMode === 'trial') {
        // Trial-with-plan: a choice is required while plans are listed;
        // with an empty listing the account goes out without planId on
        // the legacy branch.
        if (this.filteredPlans.length > 0 && !this.selectedPlan) {
          this.errorMsg = 'Selecciona un plan para continuar.';
          return;
        }
        if (this.selectedPlan) {
          this.confirmedPlanName = this.selectedPlan.planName || this.selectedPlan.name;
        }
        this.createAccountTrial();
        return;
      }
      this.errorMsg = 'Selecciona un plan para continuar.';
      return;
    }
  }

  prev(): void {
    if (this.currentStep === WizardStep.PLAN) {
      this.currentStep = WizardStep.DATOS;
      return;
    }
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  // Trial-with-plan choice (R3): remembers the chosen C1 plan for signup.
  selectPlan(planId: string): void {
    var found: Plan = null;
    var pool: Plan[] = this.filteredPlans.length > 0 ? this.filteredPlans : this.availablePlans;
    pool.forEach(function(p: Plan) {
      var key: string = p.planId || p.id;
      if (!found && key === planId) { found = p; }
    });
    this.selectedPlan = found;
    this.selectedPlanMode = 'trial';
    this.errorMsg = null;
  }

  // Bullets render ONLY from feature flags (R3): a bullet shows only when
  // its flag is explicitly true; plans without flags render none. Tolerates
  // the real C1 shape (features as string array) plus the legacy mocks
  // (features as boolean-flag object).
  bulletsForPlan(plan: Plan): string[] {
    if (!plan) { return []; }
    var features: any = (plan as any).features;
    if (!features) { return []; }
    var bullets: string[] = [];
    if (Array.isArray(features)) {
      features.forEach(function(key: string) {
        if (BULLET_MAP[key]) { bullets.push(BULLET_MAP[key]); }
      });
      return bullets;
    }
    Object.keys(BULLET_MAP).forEach(function(key: string) {
      if (features[key] === true) { bullets.push(BULLET_MAP[key]); }
    });
    return bullets;
  }

  // Landing parity: resolves the canonical tier (delegates to the shared
  // providers/plan-display source).
  canonicalTier(plan: Plan): string {
    return resolveCanonicalTier(plan);
  }

  // Landing parity: card description. Backend C1 `description` is primary;
  // tier copy is the fallback for old backends that omit display fields.
  taglineForPlan(plan: Plan): string {
    return taglineForPlan(plan, this.planDescription(plan));
  }

  // Landing parity: card feature list. Backend C1 `featuresDisplay` is
  // primary (rendered as included bullets); tier copy is the fallback.
  featuresForPlan(plan: Plan): { text: string; included: boolean }[] {
    var shared = featuresForPlan(plan);
    if (shared) { return shared; }
    var bullets: string[] = this.bulletsForPlan(plan);
    return bullets.map(function(b: string) { return { text: b, included: true }; });
  }

  // Landing parity: big card price from the payload only. Mensual shows the
  // raw amount; anual shows the monthly equivalent (total / 12).
  priceDisplay(plan: Plan): string {
    return priceDisplayFor(plan);
  }

  // Raw anual total for the 'Facturado anual' line (payload only).
  annualTotal(plan: Plan): string {
    return annualTotalFor(plan);
  }

  switchBillingCycle(cycle: 'mensual' | 'anual'): void {
    this.billingCycle = cycle;
    this.isLoadingPayPal = false;
    this.errorMsg = null;
    // Tabs fix: keep selection coherent with the visible cycle. Trial-with-plan
    // requires an explicit choice (R3), so a selection hidden by the cycle
    // switch is cleared instead of auto-selected.
    var visible: Plan[] = this.filteredPlans;
    var stillVisible = false;
    if (this.selectedPlan) {
      var key: string = this.selectedPlan.planId || this.selectedPlan.id;
      for (var i = 0; i < visible.length; i++) {
        if ((visible[i].planId || visible[i].id) === key) { stillVisible = true; break; }
      }
    }
    if (!stillVisible) {
      this.selectedPlan = null;
    }
  }

  // Trial creation: single signup path {plan:'trial', planId}.
  // planId is omitted when no plan was chosen (legacy branch compat).
  private createAccountTrial(): void {
    if (this.isLoading) return;
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(function(key) { this.form.get(key).markAsTouched(); }.bind(this));
      this.errorMsg = 'Completa nombre, email y contraseña.';
      return;
    }
    this.isLoading = true;
    this.errorMsg = null;
    var payload: any = {
      name: this.form.get('name').value,
      email: this.form.get('email').value,
      password: this.form.get('password').value,
      pass: this.form.get('password').value,
      plan: 'trial'
    };
    if (this.selectedPlan) {
      payload.planId = this.selectedPlan.planId || this.selectedPlan.id;
    }
    this.doCreateAccount(payload);
  }

  private doCreateAccount(payload: any): void {
    var request$: Observable<any> = (this.auth as any).createAccountWithSetup
      ? (this.auth as any).createAccountWithSetup(payload)
      : this.auth.registro(payload);
    request$
      .catch(function(err) { return Observable.throw(err); })
      .subscribe(
        function(res: any) {
          var data = res && res.data ? res.data : res;
          var userToStore = data.user || data;
          if (data.token) { userToStore.token = data.token; }
          var persist$ = this.auth.loginIn(userToStore);
          if (persist$ && typeof persist$.subscribe === 'function') {
            persist$.subscribe(
              function() {
                try { if (data.token) { localStorage.setItem('token', data.token); localStorage.setItem(this.auth.token_key, JSON.stringify(userToStore)); } } catch (e) {}
                this.handleCreateSuccess(data);
                if (payload.plan === 'paid' && (this.auth as any).subscriptionChanged) {
                  try { (this.auth as any).subscriptionChanged.next(); } catch(e) {}
                }
              }.bind(this),
              function(storeErr) {
                console.error('Storage error', storeErr);
                try { if (data.token) { localStorage.setItem('token', data.token); } } catch (e) {}
                this.handleCreateSuccess(data);
              }.bind(this)
            );
          } else {
            try { if (data.token) { localStorage.setItem('token', data.token); } } catch (e) {}
            this.handleCreateSuccess(data);
          }
        }.bind(this),
        function(err) {
          this.isLoading = false;
          console.error('createAccount error', err);
          if (err && err.error && err.error.message) { this.errorMsg = err.error.message; }
          else if (err && err.message) { this.errorMsg = err.message; }
          else { this.errorMsg = 'No se pudo crear la cuenta. Verifica los datos e intenta nuevamente.'; }
          if (this.auth && typeof this.auth.checkError === 'function') { this.auth.checkError(err); }
        }.bind(this)
      );
  }

  private handleCreateSuccess(data: any): void {
    this.isLoading = false;
    this.responseData = data;
    // The 201 surfaces the created circuito identity (absent on the
    // legacy branch, where both fields stay null).
    this.createdCircuitoPlanId = null;
    this.createdCircuitoGuid = null;
    var circuito: any = (data && data.circuito) || (data && data.user && data.user.circuito) || null;
    if (circuito) {
      if (circuito.planId) { this.createdCircuitoPlanId = circuito.planId; }
      if (circuito.guid) { this.createdCircuitoGuid = circuito.guid; }
    }
    if (data.subscriptionExpiresAt) { this.trialEndsAt = new Date(data.subscriptionExpiresAt); }
    else if (data.trialEndsAt) { this.trialEndsAt = new Date(data.trialEndsAt); }
    else if (data.user && data.user.trialEndsAt) { this.trialEndsAt = new Date(data.user.trialEndsAt); }
    else if (data.user && data.user.subscriptionExpiresAt) { this.trialEndsAt = new Date(data.user.subscriptionExpiresAt); }
    else { var d = new Date(); d.setDate(d.getDate() + 7); this.trialEndsAt = d; }
    var diff = this.trialEndsAt.getTime() - Date.now();
    this.trialDaysRemaining = Math.max(1, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    this.currentStep = WizardStep.CONFIRM;
    window.scrollTo(0, 0);
  }

  goToLogin(): void { this.router.navigate(['/login']); }

  get trialEndsAtLabel(): string {
    if (!this.trialEndsAt) { return ''; }
    var d = this.trialEndsAt;
    var dd = ('0' + d.getDate()).slice(-2);
    var mm = ('0' + (d.getMonth() + 1)).slice(-2);
    var yyyy = d.getFullYear();
    return dd + '/' + mm + '/' + yyyy;
  }
}



// WEBPACK FOOTER //
// ./src/app/admin/register-wizard/register-wizard.component.ts